import { LitElement, html, css } from 'lit';

import Localization from 'synapse-js/localization/localization';
import { withLocalization } from 'synapse-js/localization/withLocalization';

import 'synapse-js/ui-components/synapse-data-grid';
import 'synapse-js/ui-components/synapse-icon';
import 'synapse-js/ui-components/synapse-spinner';

import { getDefaultFields } from '../utils/devices-selector-table-fields.js';
import tableStyles from '../styles/devices-tables-style.js';
import { synapseColors, polarisColors, theme } from '../styles/index.js';
import pickerMessageHandlerFactory from '../services/device-picker-message-factory.js';
import { formatCDSDevicesToAdaptedDevices } from '../utils/device-formatter.js';
import { FIRST } from '../constants/pagination.js';
import CommonDeviceService from '../services/common-device-service.js';
import SubscriptionService from '../services/websocket/subscription-service.js';
import { deviceChangeSubscription } from '../utils/graphql/subscriptions.js';
import { deviceMessageTypeCalculator } from '../services/device-message-type-calculator.js';
import './tables/table-pagination/table-pagination.js';
import { getFieldsQuery } from '../utils/graphql/queries.js';

/**
 * @typedef {object} Options
 * @property {string} restEndpoint
 * @property {string} graphqlHost
 */

/**
 * @callback renderHoverActions
 * @param {import('../types/adapted-device').AdaptedDevice} row
 * @returns {void}
 */

/**
 * @typedef {object} Renders
 * @property {renderHoverActions} [renderHoverActions]
 */

/** This component can be used to get the current user's devices
 *  and show them in a selectable table view.
 *  The devices will be fetched once right after the component load
 *  and every selection change will trigger a device-selected event
 *  containing the list of the selected devices
 *
 * Example usage:
 *
 *  import devices-component/devices-selector
 *
 *  <devices-selector-table
 *    @device-selected=${deviceSelectedHandler}
 *    @row-clicked=${rowClickedHandler}
 *    @group-clicked=${ ({detail: {groupIds}}) => {} }
 *    token="jwtToken"
 *    theme="synapse-light"
 *    .options=${{
 *      restEndpoint: 'https://rest.endpoint',
 *      graphqlHost: 'cds.dev01-console.gotoresolve.com',
 *    }}
 *    .renders=${{
 *      renderHoverActions: renderHoverActionsFn
 *    }}
 *    .selected-device-ids=${['device-id-1', 'device-id-2']}
 *    .selectedDeviceIds=${ids}
 *    .filters=${{ platform: 'Mac'}}
 *    query="GraphQL Query{}"
 *    group-by-device-groups
 *  ></devices-selector-table>
 *
 * where theme can be: 'synapse-light' or 'synapse-dark'
 * and the rest-endpoint: "https://devices.<environment>-console.gotoresolve.com"
 *
 * renderHoverActions callback parameter is row
 *
 * You can select rows by default using property
 *    .selectedDeviceIds=${['id-1', 'id-2']}
 *  or using attribute
 *    selected-device-ids=${JSON.stringify(['id-1', 'id-2'])}
 *
 * */

export default class DevicesSelectorTable extends withLocalization(LitElement) {
  static get properties() {
    return {
      /* MANDATORY - A standard synapse JWT token */
      token: { type: String, attribute: 'token' },
      devices: { type: Array },
      groups: { type: Array },
      selectedDevices: { type: Array, attribute: 'selected-devices', reflect: true },
      /** OPTIONAL - devices with the given IDs are selected in the table by default */
      selectedDeviceIds: { type: Array, attribute: 'selected-device-ids' },
      selectedGroupIds: { type: Array, attribute: 'selected-group-ids' },
      /** OPTIONAL - you can filter devices by backend */
      filters: { type: Object, attribute: 'filter' },
      /* OPTIONAL - Columns (headers) of the table, can be set externally */
      fields: { type: Object },
      /* MANDATORY - Synapse theme ex.:['synapse-light, synapse-dark'] */
      theme: { type: String, attribute: 'theme' },
      loading: { type: Boolean },
      language: { type: String },
      /* OPTIONAL - Can be used to pass a search criteria externally for BACKEND search.
      Debounce search text changes! */
      searchText: { type: String, attribute: 'search-text', reflect: true },
      /** MANDATORY - The options contains details to fetch devices and connect mqtt.
       * ex.: options.restEndpoint: https://devices.dev01-console.gotoresolve.com
       * ex.: options.mqttEndpoint: wss://devices-iot.dev01-console.gotoresolve.com
       * ex.: options.mqttAuthorizerName: syn-dev01-afr-unattended
       * ex.: options.namespace: syn-dev01-afr-unattended
       * ex.: options.graphqlHost: cds.dev01-console.gotoresolve.com */
      options: { type: Object },
      /* OPTIONAL - renders.renderHoverActions if provided it will show action buttons on row with hover effect */
      renders: { type: Object },
      /* OPTIONAL - Groups table content by device groups */
      groupByDeviceGroups: { type: Boolean, attribute: 'group-by-device-groups' },
      /* OPTIONAL - Disable group selector */
      disableGroupSelector: { type: Boolean, attribute: 'disable-group-selector' },
      totalCount: { type: Number, attribute: false },
      pageStart: { type: Number, attribute: false },
      pageEnd: { type: Number, attribute: false },
      pageSize: { type: Number, attribute: false },
      query: { type: String },
    };
  }

  static get styles() {
    return [
      synapseColors,
      polarisColors,
      theme,
      tableStyles,
      css`
        .loader {
          width: 100%;
          height: 100%;
          box-sizing: border-box;
          display: flex;
          justify-content: center;
        }

        table-pagination {
          padding-top: 1em;
        }
      `,
    ];
  }

  constructor() {
    super();

    this.token = null;

    /** @type { import('../types/adapted-device').AdaptedDevice[]} */
    this.devices = null;

    /** @type {DeviceGroup[]} */
    this.groups = null;

    /** @type {string[]} */
    this.selectedGroupIds = [];

    /** @type { import('../types/adapted-device').AdaptedDevice[]} */
    this.selectedDevices = [];

    /** @type { string[] } */
    this.selectedDeviceIds = [];

    this.fields = null;
    this.language = '';
    this.loading = false;

    /** @type {Options} */
    this.options = {};

    /** @type {Renders} */
    this.renders = {};

    this.filters = {};
    this.defaultDeviceFilter = { verified: true };

    this.groupByDeviceGroups = false;
    this.disableGroupSelector = false;

    this.searchText = '';
    this.query = getFieldsQuery();

    this.totalCount = 0;
    this.pageSize = 5;
    this.pageStart = 0;
    this.pageEnd = this.pageSize - 1;
  }

  async connectedCallback() {
    super.connectedCallback();

    await this.loadLocalization();
    if (!this.fields) {
      this.fields = getDefaultFields((key, ...args) => this.localization(key, ...args));
    }

    this.cds = new CommonDeviceService(this.token, `https://${this.options.graphqlHost}/graphql`);
    try {
      await this.loadInitialData();
      this.graphqlSubscribe();
    } catch (e) {
      console.error(e);
    }
  }

  updated(changedProperties) {
    if (
      changedProperties.has('devices') &&
      !this.selectedDevices.length &&
      this.devices &&
      this.selectedDeviceIds.length
    ) {
      this.selectedDevices = this.devices.filter(device => this.selectedDeviceIds.includes(device.id));
    }

    // search text set
    if (changedProperties.has('searchText') && this.searchText) {
      this.devices = null;
      this.getDevices();
    }

    // search text unset
    if (changedProperties.has('searchText') && changedProperties.get('searchText') && !this.searchText) {
      this.devices = null;
      this.getDevices();
    }

    if (changedProperties.has('filters') && changedProperties.get('filters')) {
      this.devices = null;
      this.getDevices();
    }
  }

  get deviceGroupNames() {
    const selectedGroupNames = this.groups
      ?.filter(group => this.selectedGroupIds.includes(group.zeroTrustId))
      ?.map(group => group.name);

    return [
      ...(selectedGroupNames || []),
      ...(this.selectedGroupIds.includes(null) ? [this.localization('notInGroup')] : []),
    ];
  }

  render() {
    return html`<div part="selector-container" class="container">
      ${this.devices && this.fields
        ? html`<synapse-data-grid
              class="limited-col-width"
              multi-select
              more-btn-text=${this.localization('showMoreButtonText')}
              no-data-text=${this.searchText ? this.localization('noDevice') : this.localization('noTier')}
              theme=${this.theme}
              with-column-selector
              use-local-sort
              .fields=${this.fields}
              .data=${this.devices}
              .selectedRowIds=${this.selectedDeviceIds}
              .selectedGroupIds=${this.deviceGroupNames}
              enable-drill-down
              .tableEvents="${{
                selectionChange: e => {
                  this.rowSelected(
                    e.detail.selectedRows.map(row => row.data).filter(data => data),
                    e.detail.previouslySelectedRows.map(row => row.data),
                  );
                },
                rowClicked: row => this.rowClicked(row),
                ...(this.renders.renderHoverActions && { renderActions: row => this.renders.renderHoverActions(row) }),
                groupClicked: groupEvent => this.groupClicked(groupEvent.groupIds),
              }}"
              ?loading=${this.loading}
              .groupBy=${this.grouping()}
              page-start="${this.pageStart}"
              page-end="${this.pageEnd}"
              ?pagination-groupping=${this.groupByDeviceGroups}
              ?with-group-selector=${!this.disableGroupSelector}
            ></synapse-data-grid>
            <table-pagination
              count=${this.devices.length}
              total-count=${this.totalCount}
              @change=${this.onPageChange}
              @page-size-set=${this.onPageSizeSet}
              type="picker"
              @load-more-data=${this.getDevices}
            >
            </table-pagination>`
        : html`<div class="loader"><synapse-spinner></synapse-spinner></div>`}
    </div>`;
  }

  onPageChange({ detail: { start, end } }) {
    this.pageStart = start;
    this.pageEnd = end;
  }

  onPageSizeSet({ detail: { pageSize } }) {
    this.pageSize = pageSize;
  }

  grouping() {
    if (this.groupByDeviceGroups) {
      return device => {
        if (device.deviceGroupId) {
          return (this.groups || []).find(group => group?.zeroTrustId === device.deviceGroupId)?.name;
        }

        if (!device.verified) {
          return this.localization('unverified');
        }

        return this.localization('notInGroup');
      };
    }

    return null;
  }

  /** This function will send the event when the selection changes.
   *  This event will contain the list of selected devices in the detail property.
   * */
  rowSelected(selected, previousSelection) {
    this.dispatchEvent(
      new CustomEvent('device-selected', { detail: { selected, previousSelection }, bubbles: true, composed: true }),
    );
    this.selectedDevices = selected;
    this.selectedDeviceIds = Array.from(new Set(selected.map(device => device.id)));
  }

  /** This function will send the event when the row clicked.
   *  This event will contains the row in the detail property.
   * */
  // eslint-disable-next-line class-methods-use-this
  rowClicked(row) {
    return new CustomEvent('row-clicked', { detail: { row }, bubbles: true, composed: true });
  }

  /**
   *
   * @param {string[]} selectedGroupNames
   * @returns
   */
  groupClicked(selectedGroupNames) {
    const possibleGroups = [...this.groups, { name: this.localization('notInGroup'), zeroTrustId: null }];
    this.selectedGroupIds = possibleGroups
      .filter(group => selectedGroupNames.includes(group.name))
      .map(group => group.zeroTrustId);

    this.selectedDevices = this.devices.filter(device => this.selectedGroupIds.includes(device.deviceGroupId ?? null));
    this.selectedDeviceIds = this.selectedDevices.map(device => device.id);

    return new CustomEvent('group-clicked', {
      detail: { groupIds: this.selectedGroupIds },
      bubbles: true,
      composed: true,
    });
  }

  async loadLocalization() {
    const localization = Localization.getInstance();

    await localization
      .init(
        [
          { name: 'English', short: 'en-EN' },
          { name: 'Deutsch', short: 'de-DE' },
          { name: 'Español', short: 'es-ES' },
          { name: 'Français', short: 'fr-FR' },
          { name: 'Italiano', short: 'it-IT' },
          { name: 'Português (Brasil)', short: 'pt-BR' },
        ],
        this.options.localizationUrl,
      )
      .load(this.language);
  }

  async loadInitialData() {
    if (!this.options.graphqlHost) {
      throw new Error('Provide GraphQL endpoint!');
    }

    await Promise.all([this.getDevices(), this.getInitialGroups()]);
  }

  graphqlSubscribe() {
    this.subscriptionService = new SubscriptionService(
      this.options.graphqlHost,
      `wss://${this.options.graphqlHost}/graphql/realtime`,
      this.token,
    );

    this.subscriptionService.subscribeToGraphql(
      deviceChangeSubscription('"null"'),
      this.deviceChangedEventHandler(this.subscribeToDeviceNotifications),
    );
  }

  deviceChangedEventHandler() {
    return {
      next:
        /**
         *
         * @param { { data: { notifyDeviceEvent: import('synapse-common-device-service').OnDeviceChanged }}} param0
         */
        ({ data }) => {
          if (data.notifyDeviceEvent) {
            const messageType = deviceMessageTypeCalculator(
              data.notifyDeviceEvent.type,
              data.notifyDeviceEvent.device,
              data.notifyDeviceEvent.previousDevice,
            );

            const messageHandler = pickerMessageHandlerFactory(messageType);
            const { newDevices, newTotalCount } = messageHandler.handleMessage(
              data.notifyDeviceEvent,
              this.devices,
              this.totalCount,
            );

            this.devices = newDevices;
            this.totalCount = newTotalCount;
          }
        },
      error: err => {
        console.error('Error in device changed subscription', err);
      },
      complete: () => {
        console.warn('Device changed subscription invalidated!');
      },
    };
  }

  precheckRows() {
    // selects the devices on Init time for the groups
    this.selectedDevices = this.devices.filter(device => this.selectedGroupIds.includes(device.deviceGroupId ?? null));
    this.selectedDeviceIds = [...this.selectedDeviceIds, this.selectedDevices.map(device => device.id)];
  }

  /**
   *
   * @param {{detail?: {offset?: number}}} event
   */
  async getDevices(event) {
    this.loading = true;

    try {
      const { getDevices, getAggregate } = await this.cds.getDevices(
        {
          first: FIRST,
          offset: event?.detail?.offset || 0,
          sort: { deviceGroupId: 'desc' },
          ...(this.searchText ? { searchText: this.searchText } : {}),
          ...(Object.keys(this.filters).length
            ? { ...this.defaultDeviceFilter, ...this.filters }
            : this.defaultDeviceFilter),
        },
        {},
        this.query,
      );
      const updatedDevices = formatCDSDevicesToAdaptedDevices(getDevices);

      this.devices = this.searchText ? updatedDevices : [...(this.devices || []), ...updatedDevices];
      this.totalCount = getAggregate?.totalDeviceCount || 0;

      if (this.groups?.length) {
        this.precheckRows();
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
    }
  }

  async getInitialGroups() {
    try {
      this.groups = await this.cds.getGroups();
      if (this.devices?.length) {
        this.precheckRows();
      }
    } catch (error) {
      console.error(error);
    }
  }
}

window.customElements.define('devices-selector-table', DevicesSelectorTable);
